import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Certificate } from 'server/shared/models/certificate';
import { CertificateGroup } from 'server/shared/models/certificateGroup';
import { MappedCertificate } from 'server/shared/models/user';
import { queryParam$ } from '../../../helpers/queryParam$';

@Component({
  selector: 'app-certificate-multiselect',
  templateUrl: './certificate-multiselect.component.html',
  styleUrls: ['./certificate-multiselect.component.scss'],
})
export class CertificateMultiselectComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  @Output() selectionChanged = new EventEmitter<Certificate[]>();

  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() hideAllOption: boolean = false;
  @Input() label?: string;
  @Input() useQueryParam: string | false = false;
  // Use getter/setter below for allCertificates
  _selectedCertificates: Certificate[] = [];
  _selectedCertificateIds: (string | 'ALL')[] = [];

  @Input() set selectedCertificates(certificates: Certificate[]) {
    this._selectedCertificates = certificates || [];
    this.updateSelectedIds();
  }

  @Input() set selectedMappedCertificates(
    mappedCertificates: MappedCertificate[],
  ) {
    if (mappedCertificates?.length && this.allCertificates.length) {
      const certificateIds = mappedCertificates.map((cert) => cert.id);
      let certificatesAndGroups = [
        ...this.allCertificates,
        ...this.certificateGroups.map((group) => ({
          _id: group.id,
          name: group.displayName,
        })),
      ];
      this._selectedCertificates = certificatesAndGroups.filter((cert) =>
        certificateIds.includes(cert._id),
      );
    } else if (this.allCertificates.length) {
      if (!this.hideAllOption) {
        this._selectedCertificates = [];
        this._selectedCertificateIds = ['ALL'];
      } else {
        this._selectedCertificateIds = [];
        this._selectedCertificates = [];
      }
      return;
    }
    this.updateSelectedIds();
  }

  private lastQueryParamValue: string | null = null;
  private suppressParamSync = false;

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    if (this.useQueryParam) {
      queryParam$(this.route, this.useQueryParam)
        .pipe()
        .subscribe((param: string) => {
          this.lastQueryParamValue = param;
          if (!this.suppressParamSync) {
            this.applySelectionFromParamAndCertificates();
          }
        });
    } else {
      // Default to ALL selected if none are specified
      if (!this._selectedCertificateIds.length) {
        this._selectedCertificateIds = ['ALL'];
        this._selectedCertificates = [];
      }
    }
  }

  @Input() set allCertificates(certs: Certificate[]) {
    this._allCertificates = certs;
    if (!this.suppressParamSync) {
      this.applySelectionFromParamAndCertificates();
    }
  }
  get allCertificates(): Certificate[] {
    return this._allCertificates;
  }
  private _allCertificates: Certificate[] = [];

  @Input() set certificateGroups(certs: CertificateGroup[]) {
    this._certificateGroups = certs;
    if (!this.suppressParamSync) {
      this.applySelectionFromParamAndCertificates();
    }
  }
  get certificateGroups(): CertificateGroup[] {
    return this._certificateGroups;
  }
  private _certificateGroups: CertificateGroup[] = [];

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectionChange(selectedIds: (string | 'ALL')[]): void {
    this.suppressParamSync = true;
    // If 'ALL' is currently selected and user selects a specific, remove 'ALL'
    if (
      this._selectedCertificateIds.includes('ALL') &&
      selectedIds.length > 1
    ) {
      selectedIds = selectedIds.filter((id) => id !== 'ALL');
    }
    // Mutually exclusive logic for 'ALL'
    if (selectedIds.includes('ALL')) {
      this._selectedCertificateIds = ['ALL'];
      this._selectedCertificates = [];
    } else {
      this._selectedCertificateIds = selectedIds.filter((id) => id !== 'ALL');
      const certificatesAndGroups = [
        ...this.allCertificates,
        ...this.certificateGroups.map((group) => ({
          _id: group.id,
          name: group.displayName,
        })),
      ];
      this._selectedCertificates = certificatesAndGroups.filter((cert) =>
        this._selectedCertificateIds.includes(cert._id),
      );
    }
    this.emitValue();
    setTimeout(() => {
      this.suppressParamSync = false;
    }, 0);
  }

  private updateSelectedIds(): void {
    this._selectedCertificateIds = this._selectedCertificates.map(
      (cert) => cert._id,
    );
  }

  private applySelectionFromParamAndCertificates(): void {
    const param = this.lastQueryParamValue;
    if (param) {
      const ids = param.split(',');
      if (ids.includes('ALL')) {
        this._selectedCertificateIds = ['ALL'];
        this._selectedCertificates = [];
      } else {
        this._selectedCertificateIds = ids;

        const certificatesAndGroups = [
          ...this._allCertificates,
          ...this.certificateGroups.map((group) => ({
            _id: group.id,
            name: group.displayName,
          })),
        ];
        this._selectedCertificates = certificatesAndGroups.filter((cert) =>
          this._selectedCertificateIds.includes(cert._id),
        );
      }
    } else {
      this._selectedCertificateIds = ['ALL'];
      this._selectedCertificates = [];
    }
  }

  private emitValue(): void {
    // Only emit the actual certificate objects (not 'ALL')
    this.selectionChanged.emit(this._selectedCertificates);

    if (this.useQueryParam) {
      const paramValue =
        this._selectedCertificateIds.length > 0
          ? this._selectedCertificateIds.join(',')
          : null;

      this.updateQueryParams(paramValue);
    }
  }

  private updateQueryParams(paramValue: string | null): void {
    const queryParams: any = {};
    queryParams[this.useQueryParam || ''] = paramValue;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
